import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/input';
import { Phone, Plus, Search, RefreshCw, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { usePhoneNumbers, useCreatePhoneNumbers } from '../hooks/useApi';
import { useToast } from '../components/ui/toaster';
import type { PhoneNumber, CreatePhoneNumberRequest } from '../services/api';

export default function PhoneNumbers() {
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);

    const { data: phoneNumbers, isLoading, error, refetch } = usePhoneNumbers();
    const createPhoneNumbersMutation = useCreatePhoneNumbers();
    const { toast } = useToast();

    const filteredNumbers = phoneNumbers?.filter(number =>
        number.displayPhoneNumber.includes(searchTerm) ||
        number.verifiedName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        number.wabaAccountId.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

    const handleCreatePhoneNumbers = async (data: CreatePhoneNumberRequest) => {
        try {
            await createPhoneNumbersMutation.mutateAsync(data);
            toast({
                title: 'Sucesso',
                description: 'Números de telefone criados com sucesso!',
                variant: 'success',
            });
            setShowCreateForm(false);
        } catch (error) {
            toast({
                title: 'Erro',
                description: 'Falha ao criar números de telefone',
                variant: 'destructive',
            });
        }
    };

    const handleRefresh = () => {
        refetch();
        toast({
            title: 'Atualizado',
            description: 'Lista de números atualizada',
            variant: 'success',
        });
    };

    const parseThroughput = (throughput?: string): string | null => {
        if (!throughput) return null;
        try {
            const parsed = JSON.parse(throughput);
            return parsed.level || throughput;
        } catch {
            return throughput;
        }
    };

    const getQualityRatingColor = (rating?: string) => {
        switch (rating) {
            case 'GREEN':
            case 'HIGH':
                return 'success';
            case 'YELLOW':
            case 'MEDIUM':
                return 'secondary';
            case 'RED':
            case 'LOW':
                return 'destructive';
            case 'UNKNOWN':
                return 'default';
            default:
                return 'default';
        }
    };

    const getQualityRatingIcon = (rating?: string) => {
        switch (rating) {
            case 'GREEN':
            case 'HIGH':
                return <CheckCircle className="w-4 h-4 text-green-600" />;
            case 'YELLOW':
            case 'MEDIUM':
                return <Clock className="w-4 h-4 text-yellow-600" />;
            case 'RED':
            case 'LOW':
                return <AlertCircle className="w-4 h-4 text-red-600" />;
            case 'UNKNOWN':
            default:
                return <AlertCircle className="w-4 h-4 text-gray-400" />;
        }
    };

    const getQualityRatingLabel = (rating?: string) => {
        switch (rating) {
            case 'GREEN':
                return 'Verde';
            case 'YELLOW':
                return 'Amarelo';
            case 'RED':
                return 'Vermelho';
            case 'UNKNOWN':
                return 'Desconhecido';
            case 'HIGH':
                return 'Alta';
            case 'MEDIUM':
                return 'Média';
            case 'LOW':
                return 'Baixa';
            default:
                return rating || 'N/A';
        }
    };

    const getThroughputColor = (throughput?: string) => {
        const parsed = parseThroughput(throughput);
        switch (parsed) {
            case 'STANDARD':
                return 'secondary';
            case 'HIGH':
                return 'success';
            case 'NOT_APPLICABLE':
                return 'default';
            default:
                return 'default';
        }
    };

    const getThroughputLabel = (throughput?: string) => {
        const parsed = parseThroughput(throughput);
        switch (parsed) {
            case 'STANDARD':
                return 'Padrão';
            case 'HIGH':
                return 'Alto';
            case 'NOT_APPLICABLE':
                return 'N/A';
            default:
                return parsed || 'N/A';
        }
    };

    if (error) {
        return (
            <div className="text-center py-8">
                <p className="text-red-600">Erro ao carregar números: {error.message}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold text-blue-600">Números de Telefone</h1>
                    <p className="text-gray-600">Gerencie números WhatsApp Business</p>
                </div>
                <div className="flex space-x-2">
                    <Button variant="outline" onClick={handleRefresh} disabled={isLoading} className="border-blue-500 text-blue-600 hover:bg-blue-50 hover:border-blue-600">
                        <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                        Atualizar
                    </Button>
                    <Button onClick={() => setShowCreateForm(true)} className="bg-blue-600 hover:bg-blue-700">
                        <Plus className="w-4 h-4 mr-2" />
                        Buscar Números
                    </Button>
                </div>
            </div>

            {/* Search */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-500 w-4 h-4" />
                <Input
                    placeholder="Buscar números..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 border-blue-200 focus:border-blue-500 focus:ring-blue-500"
                />
            </div>

            {/* Create Phone Numbers Form */}
            {showCreateForm && (
                <CreatePhoneNumbersForm
                    onSubmit={handleCreatePhoneNumbers}
                    onCancel={() => setShowCreateForm(false)}
                    isLoading={createPhoneNumbersMutation.isPending}
                />
            )}

            {/* Phone Numbers List */}
            {isLoading ? (
                <div className="text-center py-8">Carregando números...</div>
            ) : filteredNumbers.length === 0 ? (
                <Card className="border-blue-200">
                    <CardContent className="text-center py-8">
                        <Phone className="w-12 h-12 text-blue-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-blue-600 mb-2">Nenhum número encontrado</h3>
                        <p className="text-gray-500 mb-4">
                            {searchTerm ? 'Tente ajustar sua busca' : 'Busque números disponíveis em suas contas WABA'}
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {filteredNumbers.map((number) => (
                        <Card key={number.publicId} className="hover:shadow-md hover:border-blue-300 transition-all border-blue-100">
                            <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                        <Phone className="w-5 h-5 text-blue-600" />
                                        <CardTitle className="text-lg text-blue-700">{number.displayPhoneNumber}</CardTitle>
                                    </div>
                                    {getQualityRatingIcon(number.qualityRating)}
                                </div>
                                <CardDescription>
                                    {number.verifiedName || 'Nome não verificado'}
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    {/* Quality Rating */}
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-600">Qualidade:</span>
                                        <Badge variant={getQualityRatingColor(number.qualityRating) as any}>
                                            {getQualityRatingLabel(number.qualityRating)}
                                        </Badge>
                                    </div>

                                    {/* Throughput */}
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-600">Throughput:</span>
                                        <Badge variant={getThroughputColor(number.throughput) as any}>
                                            {getThroughputLabel(number.throughput)}
                                        </Badge>
                                    </div>

                                    {/* Messaging Limit */}
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-600">Limite:</span>
                                        <span className="text-sm font-medium">
                                            {number.messagingLimitTier || 'N/A'}
                                        </span>
                                    </div>

                                    {/* WABA Account */}
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-600">WABA:</span>
                                        <span className="text-xs font-mono text-gray-500">
                                            {number.wabaAccountId.slice(0, 8)}...
                                        </span>
                                    </div>

                                    {/* Created Date */}
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-600">Criado:</span>
                                        <span className="text-sm">
                                            {new Date(number.createdAt).toLocaleDateString()}
                                        </span>
                                    </div>

                                    {/* WhatsApp Account Link */}
                                    {number.whatsappAccountId && (
                                        <div className="pt-2 border-t">
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-gray-600">Conta WhatsApp:</span>
                                                <Badge variant="outline" className="text-xs">
                                                    Conectada
                                                </Badge>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Stats */}
            {phoneNumbers && phoneNumbers.length > 0 && (
                <Card className="border-blue-100 bg-gradient-to-br from-white to-blue-50/30">
                    <CardHeader>
                        <CardTitle className="text-blue-600">Estatísticas</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            <div className="text-center">
                                <div className="text-2xl font-bold text-green-600">
                                    {phoneNumbers.filter(n => n.qualityRating === 'GREEN' || n.qualityRating === 'HIGH').length}
                                </div>
                                <div className="text-sm text-gray-600">Verde/Alta</div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-yellow-600">
                                    {phoneNumbers.filter(n => n.qualityRating === 'YELLOW' || n.qualityRating === 'MEDIUM').length}
                                </div>
                                <div className="text-sm text-gray-600">Amarelo/Média</div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-red-600">
                                    {phoneNumbers.filter(n => n.qualityRating === 'RED' || n.qualityRating === 'LOW').length}
                                </div>
                                <div className="text-sm text-gray-600">Vermelho/Baixa</div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-gray-600">
                                    {phoneNumbers.filter(n => n.qualityRating === 'UNKNOWN' || !n.qualityRating).length}
                                </div>
                                <div className="text-sm text-gray-600">Desconhecido</div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-blue-600">
                                    {phoneNumbers.filter(n => n.whatsappAccountId).length}
                                </div>
                                <div className="text-sm text-gray-600">Conectados</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}

// Create Phone Numbers Form Component
function CreatePhoneNumbersForm({
    onSubmit,
    onCancel,
    isLoading
}: {
    onSubmit: (data: CreatePhoneNumberRequest) => void;
    onCancel: () => void;
    isLoading: boolean;
}) {
    const [formData, setFormData] = useState<CreatePhoneNumberRequest>({
        wabaAccountId: '',
        accessToken: '',
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(formData);
    };

    return (
        <Card className="border-blue-200">
            <CardHeader>
                <CardTitle className="text-blue-600">Buscar Números de Telefone</CardTitle>
                <CardDescription>
                    Busque números disponíveis em uma conta WABA específica
                </CardDescription>
            </CardHeader>
            <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <Label htmlFor="wabaAccountId">WABA Account ID</Label>
                        <Input
                            id="wabaAccountId"
                            value={formData.wabaAccountId}
                            onChange={(e) => setFormData({ ...formData, wabaAccountId: e.target.value })}
                            placeholder="Ex: 123456789012345"
                            required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            ID da conta WhatsApp Business no Facebook
                        </p>
                    </div>

                    <div>
                        <Label htmlFor="accessToken">Access Token</Label>
                        <Input
                            id="accessToken"
                            type="password"
                            value={formData.accessToken}
                            onChange={(e) => setFormData({ ...formData, accessToken: e.target.value })}
                            placeholder="Token de acesso do Facebook"
                            required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Token de acesso válido para a conta WABA
                        </p>
                    </div>

                    <div className="flex justify-end space-x-2">
                        <Button type="button" variant="outline" onClick={onCancel} className="border-blue-300 text-blue-600 hover:bg-blue-50">
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={isLoading} className="bg-blue-600 hover:bg-blue-700">
                            {isLoading ? 'Buscando...' : 'Buscar Números'}
                        </Button>
                    </div>
                </form>
            </CardContent>
        </Card>
    );
}
